import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, ChatCircle, ShieldCheck, Star, Check, ArrowLeft } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../lib/analytics';
import api from '../lib/api';

export default function PremiumPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) return;
    // Fast-path: user already premium (DB confirmed) — skip polling, redirect immediately
    if (user?.is_premium) {
      setPaymentStatus('success');
      navigate('/chat', { replace: true });
      return;
    }
    let attempts = 0;
    const maxAttempts = 15; // 15 attempts × 3 seconds = 45 seconds total
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus('timeout');
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        console.log('Payment status:', data);
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPaymentError(null);
          updateUser({ is_premium: true, is_verified: true });
          track('premium_purchased', { session_id: sessionId });
          // Auto-redirect to chat after a brief success message
          setTimeout(() => navigate('/chat', { replace: true }), 1500);
          return;
        }
        if (data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }
        attempts++;
        setTimeout(poll, 3000);
      } catch (err) {
        console.error('Payment status error:', err);
        const detail = err?.response?.data?.detail;
        if (detail) {
          setPaymentError(String(detail));
          setPaymentStatus('error');
          return; // Stop polling on definitive backend error
        }
        attempts++;
        setTimeout(poll, 3000);
      }
    };
    setPaymentStatus('checking');
    // Start polling after a short delay to let Stripe process
    setTimeout(poll, 2000);
  }, [sessionId, updateUser, user?.is_premium, navigate]);

  const handleManualCheck = async () => {
    if (!sessionId) return;
    setPaymentStatus('checking');
    setPaymentError(null);
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        updateUser({ is_premium: true, is_verified: true });
        setTimeout(() => navigate('/chat', { replace: true }), 1500);
      } else {
        setPaymentStatus('pending');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) setPaymentError(String(detail));
      setPaymentStatus('error');
    }
  };

  const handleUpgrade = async () => {
    if (!user) { navigate('/auth'); return; }
    track('premium_clicked', { promo: promoResult?.valid ? promoResult.code : null });
    setLoading(true);
    setPaymentError(null);
    try {
      const origin = window.location.origin;
      console.log('[upgrade] POST /payments/create-checkout from', origin);
      const { data } = await api.post('/payments/create-checkout', {
        origin_url: origin,
        promo_code: promoResult?.valid ? promoResult.code : null
      });
      console.log('[upgrade] response', data);
      if (data?.url) {
        window.location.href = data.url;
        return; // prevent finally from re-enabling the button before the redirect
      }
      setPaymentError('Stripe did not return a checkout URL. Please try again or contact support.');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      console.error('[upgrade] failed', { status, detail, err });
      setPaymentError(
        typeof detail === 'string'
          ? `Stripe ${status || ''}: ${detail}`.trim()
          : `Checkout failed (${status || 'network error'}). Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post('/promo/validate', { code: promoCode });
      setPromoResult(data);
    } catch {
      setPromoResult({ valid: false });
    }
    setPromoChecking(false);
  };

  const features = [
    { icon: ChatCircle, title: 'Unlimited Chats', desc: 'No daily limits on new conversations' },
    { icon: ShieldCheck, title: 'Verified Badge', desc: 'Stand out with a trusted verified profile' },
    { icon: Star, title: 'Priority in Search', desc: 'Appear first in browse results' },
    { icon: Crown, title: 'Premium Member', desc: 'Exclusive access to premium features' },
  ];

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="premium-page">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={20} /> Back
        </button>

        {/* Payment Status */}
        {paymentStatus === 'success' && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 text-center mb-8 border-[#D4AF37]/20" data-testid="payment-success">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
              <Check size={32} weight="bold" className="text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-semibold text-white font-['Outfit'] mb-2">Welcome to Premium!</h2>
            <p className="text-[#9EABCE]">Your account has been upgraded successfully.</p>
          </motion.div>
        )}
        {paymentStatus === 'checking' && (
          <div className="glass-card p-8 text-center mb-8" data-testid="payment-checking">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#9EABCE]">Verifying your payment with Stripe...</p>
            <p className="text-xs text-[#9EABCE]/50 mt-2">This may take a few seconds</p>
          </div>
        )}
        {(paymentStatus === 'timeout' || paymentStatus === 'pending' || paymentStatus === 'error') && (
          <div className="glass-card p-8 text-center mb-8 border-[#D4AF37]/10" data-testid="payment-retry">
            <p className="text-white font-semibold mb-2">
              {paymentError ? 'Payment verification failed' : 'Payment processing...'}
            </p>
            {paymentError ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-left" data-testid="payment-error-detail">
                <p className="text-red-300 text-xs font-mono break-words whitespace-pre-wrap">
                  {paymentError}
                </p>
              </div>
            ) : (
              <p className="text-[#9EABCE] text-sm mb-4">Stripe is still confirming your payment. Click below to check again.</p>
            )}
            <button onClick={handleManualCheck} data-testid="manual-check-btn"
              className="btn-gold px-6 py-3 rounded-xl font-bold text-sm">
              Check Payment Status
            </button>
          </div>
        )}

        {/* Premium Card */}
        {!user?.is_premium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card overflow-hidden"
            style={{
              backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/30562ac1-6cb8-40db-b89b-be786ceb71ff/images/245a5928c317736af8d2bc94e523386ba51bcf6465f15aa47a9e2f0af42cf5ae.png)',
              backgroundSize: 'cover',
              backgroundBlendMode: 'overlay'
            }}
          >
            <div className="bg-[#060A1A]/80 p-8">
              <div className="text-center mb-8">
                <Crown size={48} weight="fill" className="text-[#D4AF37] mx-auto mb-4" />
                <h2 className="text-3xl font-semibold text-white font-['Outfit'] mb-2">
                  YuBlink <span className="text-gold-gradient">Premium</span>
                </h2>
                <div className="flex items-baseline justify-center gap-1 mt-4">
                  {promoResult?.valid ? (
                    <>
                      <span className="text-2xl text-[#9EABCE] line-through font-['Outfit']">10</span>
                      <span className="text-4xl font-bold text-[#D4AF37] font-['Outfit'] ml-2">{(10 * (1 - promoResult.discount_percent / 100)).toFixed(0)}</span>
                      <span className="text-lg text-[#D4AF37]">&euro;</span>
                      <span className="text-[#9EABCE] text-sm">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-[#D4AF37] font-['Outfit']">10</span>
                      <span className="text-lg text-[#D4AF37]">&euro;</span>
                      <span className="text-[#9EABCE] text-sm">/month</span>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {features.map(({ icon: Icon, title, desc }, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} weight="light" className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{title}</p>
                      <p className="text-[#9EABCE] text-xs">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Promo Code */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Promo code"
                    data-testid="promo-code-input"
                    className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl px-4 py-3 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={promoChecking || !promoCode.trim()}
                    data-testid="apply-promo-btn"
                    className="btn-outline-gold px-5 py-3 rounded-xl text-sm font-bold disabled:opacity-50"
                  >
                    {promoChecking ? '...' : 'Apply'}
                  </button>
                </div>
                {promoResult && (
                  <p className={`text-xs mt-2 ${promoResult.valid ? 'text-green-400' : 'text-red-400'}`} data-testid="promo-result">
                    {promoResult.valid ? `Code ${promoResult.code} applied — ${promoResult.discount_percent}% off!` : 'Invalid promo code'}
                  </p>
                )}
              </div>

              {paymentError && !sessionId && (
                <div
                  data-testid="upgrade-error-banner"
                  className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-left"
                >
                  <p className="text-red-300 text-xs font-semibold mb-1">Checkout error</p>
                  <p className="text-red-300 text-xs font-mono break-words whitespace-pre-wrap">
                    {paymentError}
                  </p>
                </div>
              )}

              <button
                onClick={handleUpgrade}
                disabled={loading}
                data-testid="premium-upgrade-btn"
                className="w-full btn-gold py-4 rounded-xl font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#060A1A] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Crown size={22} weight="fill" /> Upgrade Now
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {user?.is_premium && !paymentStatus && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 text-center border-[#D4AF37]/20">
            <Crown size={48} weight="fill" className="text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-white font-['Outfit'] mb-2">You're Premium!</h2>
            <p className="text-[#9EABCE]">Enjoy all premium features including unlimited chats and verified badge.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
